/**
 * 9Baji informational/legal page content.
 * Structure mirrors the reference help-guide pages.
 * Does not invent company registration, addresses, emails, phones, or commission rates.
 */

export const INFO_PAGE_META = {
  about_us: { title: 'About Us', type: 'about_us' },
  contact_us: { title: 'Contact Us', type: 'contact_us' },
  privacy_policy: { title: 'Privacy Policy', type: 'privacy_policy' },
  terms_conditions: { title: 'Terms & Conditions', type: 'terms_conditions' },
  rules_regulations: { title: 'Rules and Regulations', type: 'rules_regulations' },
  responsible_gaming: { title: 'Responsible Gaming', type: 'responsible_gaming' },
  faq: { title: 'Frequently Asked Questions', type: 'faq' },
  affiliate: { title: 'Affiliate', type: 'affiliate' },
  security: { title: 'Security', type: 'security' },
  age_restriction: { title: '18+', type: 'age_restriction' },
  kyc: { title: 'KYC', type: 'kyc' },
  aml: { title: 'AML', type: 'aml' },
};

export const FAQ_TOPICS = [
  { id: 'account', label: 'Account' },
  { id: 'casino', label: 'Casino' },
  { id: 'lottery', label: 'Lottery' },
  { id: 'payment', label: 'Payment' },
  { id: 'promotion', label: 'Promotion' },
  { id: 'slots', label: 'Slots' },
  { id: 'sports', label: 'Sports' },
  { id: 'table', label: 'Table' },
];

/** Safe product FAQs — no invented legal/company contact details. */
export const FALLBACK_FAQS = [
  {
    id: 'faq-account-1',
    topic: 'account',
    question: 'How do I create an 9Baji account?',
    answer:
      'Open Sign Up from the 9Baji website, complete the registration form, and confirm that you meet the age requirement. After registration you can log in and explore games.',
  },
  {
    id: 'faq-account-2',
    topic: 'account',
    question: 'I forgot my password. What should I do?',
    answer:
      'Use the Login page password-recovery option if available, or contact support through Live Chat for account help.',
  },
  {
    id: 'faq-casino-1',
    topic: 'casino',
    question: 'What games can I play on 9Baji?',
    answer:
      '9Baji offers sports betting, casino, live casino, crash, fishing, slots, and related entertainment categories available on the homepage.',
  },
  {
    id: 'faq-slots-1',
    topic: 'slots',
    question: 'How do I find slot games?',
    answer:
      'Select the Slots category from the homepage menu, then browse or search the available games.',
  },
  {
    id: 'faq-payment-1',
    topic: 'payment',
    question: 'How do deposits and withdrawals work?',
    answer:
      'After you log in, open Deposit or Withdraw from your account tools and follow the on-screen steps for the payment method you choose. Always check the latest limits and processing notes shown in the wallet screens.',
  },
  {
    id: 'faq-promotion-1',
    topic: 'promotion',
    question: 'Where can I see promotions?',
    answer:
      'Open the Promotions section from the site navigation. Read each offer’s rules carefully before joining.',
  },
  {
    id: 'faq-sports-1',
    topic: 'sports',
    question: 'Does 9Baji support sports betting?',
    answer:
      'Yes. Use the Sports category on the homepage to browse available sports products and providers.',
  },
  {
    id: 'faq-table-1',
    topic: 'table',
    question: 'Where are table games?',
    answer:
      'Open the Table category from the homepage to browse available table games.',
  },
  {
    id: 'faq-lottery-1',
    topic: 'lottery',
    question: 'Does 9Baji have lottery games?',
    answer:
      'Lottery titles appear under the Lottery category when available in the current game catalog.',
  },
];

function section(title, paragraphs = [], list = null, level = 2) {
  return { title, level, paragraphs, list };
}

export const FALLBACK_SECTIONS = {
  about_us: [
    section('About 9Baji Casino', [
      '9Baji is an online casino and sports betting platform made for players who want a simple, safe, and smooth gaming experience.',
      'Players look for easy games, clear bonus rules, fast deposits and withdrawals, and helpful support. 9Baji focuses on giving those essentials in one place.',
      'At 9Baji, players can enjoy casino games, live casino, table games, slot games, crash, fishing, sports betting, and promotions. The platform is designed to work on both mobile and desktop.',
      'Player safety matters. 9Baji works to protect account activity and keep the experience clear and controlled.',
      'Our main focus is to make online casino and sports entertainment simple to use.',
    ]),
    section('Simple Registration & Play Games Easily', [
      'New players can start using 9Baji without difficult steps. Registration is designed to be quick.',
      'After creating an account, players can explore games and promotions. The website is arranged so players can find what they need.',
      '9Baji provides clear information about games, bonuses, and rules so players can understand how to play before they start.',
      'Deposits and withdrawals use the payment options shown in the wallet screens. Always follow the on-screen steps and check the latest rules before making a transaction.',
    ]),
    section('What Does 9Baji Focus On?', [
      '9Baji focuses on giving players a better gaming experience with:',
    ], [
      'A wide range of games, including slots, live casino, table games, crash, fishing, lottery, and sports',
      'Smooth gameplay on mobile and desktop',
      'An easy registration process',
      'Clear bonus and promotion rules',
      'A guided deposit and withdrawal process',
      'Helpful customer support through Live Chat',
      'Responsible gaming awareness',
    ]),
    section('Responsible Gaming at 9Baji', [
      '9Baji supports responsible gaming.',
      'Casino and sports games should be played for entertainment only. Players should never treat games as a guaranteed way to earn money.',
      'Players should always:',
    ], [
      'Play within their budget',
      'Read all game and bonus rules',
      'Avoid chasing losses',
      'Take breaks when needed',
      'Stop playing if gaming feels stressful',
      'Never borrow money to gamble',
    ]),
    section(
      'Our Player Commitment',
      [
        '9Baji aims to give players a simple, safe, and enjoyable online experience.',
        'We want every player to understand the platform easily through clear game rules, account protection, easy payment steps, helpful support, and responsible gaming.',
        '9Baji — play smart, enjoy safely, and stay in control.',
      ],
      null,
      3,
    ),
  ],

  contact_us: [
    section('Contact Us', [
      'The 9Baji support team is ready to help players.',
      'If you have questions about your account, deposit, withdrawal, game issue, bonus, or safe gaming help, contact customer support.',
      'Use Live Chat on the 9Baji website for the fastest help.',
    ]),
    section('Customer Support', [
      'Live Chat: Available on the 9Baji website for quick help.',
      'Additional contact channels may appear here when officially published for 9Baji.',
    ], null, 3),
  ],

  privacy_policy: [
    section('Privacy Policy', [
      'This page explains how 9Baji handles information you provide while using the website.',
      '9Baji collects account and usage information needed to operate the platform, process requests, and improve the service.',
      'We work to keep account details and payment activity protected with appropriate technical and organizational measures.',
      'Information may be used to provide games and wallet features, verify account activity, prevent abuse, and respond to support requests.',
      'Detailed legal wording for privacy handling will appear here when published through the 9Baji content system. Until then, use Live Chat for privacy-related questions.',
    ]),
  ],

  terms_conditions: [
    section('Terms & Conditions', [
      'These Terms & Conditions describe the rules for using 9Baji.',
      'By creating an account or using 9Baji, you confirm that you meet the minimum age requirement and that you will use the platform lawfully and responsibly.',
      'Players must provide accurate account information, protect login details, and follow the rules shown for games, promotions, deposits, and withdrawals.',
      '9Baji may update product rules, promotions, and account controls as needed to keep the platform safe and fair.',
      'Full legal terms will appear here when published through the 9Baji content system. If a rule shown in a game, promotion, or wallet screen differs from this summary, follow the on-screen rule for that action.',
    ]),
  ],

  rules_regulations: [
    section('Rules and Regulations', [
      '9Baji players must follow platform rules when creating accounts, playing games, joining promotions, and using payment features.',
      'Key expectations include:',
    ], [
      'Only one personal account per player unless 9Baji states otherwise',
      'No underage play — players must be 18 or older',
      'No abuse of bonuses, payment methods, or game systems',
      'No sharing of account access with other people',
      'Follow all on-screen rules for deposits, withdrawals, and promotions',
      'Play responsibly and within your means',
    ]),
    section('Fair Play', [
      '9Baji may review unusual account activity to protect players and the platform.',
      'If you need clarification about a rule, contact support through Live Chat.',
      'Complete published regulations will appear here when available in the 9Baji content system.',
    ]),
  ],

  responsible_gaming: [
    section('Responsible Gaming', [
      '9Baji wants players to enjoy games for entertainment while staying in control.',
      'Gambling should never be used as a way to solve financial problems or as guaranteed income.',
    ]),
    section('Helpful Habits', [], [
      'Set a budget before you play and stick to it',
      'Take regular breaks',
      'Do not chase losses',
      'Do not borrow money to gamble',
      'Keep gaming separate from daily essentials',
      'Stop immediately if play feels stressful or out of control',
    ]),
    section('Need Help?', [
      'If you need support related to responsible gaming, open Live Chat on 9Baji and ask for help.',
      '9Baji is only for players who are 18 years old or above.',
    ]),
  ],

  affiliate: [
    section('9Baji Affiliate', [
      'Share 9Baji with people you know and grow with the platform’s referral tools.',
      'After you create an 9Baji account, referral tools are available in the logged-in profile when the referral feature is enabled for your account.',
      'This page is informational only. Commission rates, payout schedules, and partner contracts are controlled by 9Baji’s existing referral systems and are not changed here.',
    ]),
    section('How To Get Started', [], [
      'Create an 9Baji account or log in',
      'Open your profile referral tools when available',
      'Share your unique link through allowed channels',
      'Follow all 9Baji rules and responsible marketing practices',
    ]),
    section('Important Notes', [
      'Do not invent or advertise commission figures that are not shown in your 9Baji referral tools.',
      'If referral tools are not visible on your account, contact support through Live Chat for guidance.',
    ]),
  ],

  security: [
    section('Security', [
      '9Baji uses account security controls to help protect player access.',
      'Keep your password private, enable available security options in your profile, and contact Live Chat if you notice unusual activity.',
    ]),
  ],

  age_restriction: [
    section('18+', [
      '9Baji is only for players who are 18 years old or above.',
      'By using 9Baji you confirm that you meet the age requirement in your jurisdiction.',
    ]),
  ],

  kyc: [
    section('KYC', [
      '9Baji may request identity or account verification to keep the platform safe and to process certain wallet requests.',
      'If verification is required, follow the instructions shown in your account or provided by support.',
    ]),
  ],

  aml: [
    section('AML', [
      '9Baji monitors unusual payment and account activity to help prevent abuse.',
      'Players must use their own payment details and follow all wallet rules shown on the site.',
    ]),
  ],
};

export function sectionsToHtml(sections) {
  if (!Array.isArray(sections)) return '';
  return sections
    .map((sec) => {
      const tag = sec.level === 3 ? 'h3' : 'h2';
      const paras = (sec.paragraphs || []).map((p) => `<p>${escapeHtml(p)}</p>`).join('');
      const list = Array.isArray(sec.list) && sec.list.length
        ? `<ul>${sec.list.map((item) => `<li>${escapeHtml(item)}</li>`).join('')}</ul>`
        : '';
      return `<${tag}>${escapeHtml(sec.title)}</${tag}>${paras}${list}`;
    })
    .join('');
}

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
